import React, { useState, useRef } from 'react';
import axios from 'axios';
import i18next from 'core/i18n';
import { useMutation } from '@apollo/client';
import { Button, palettes, DialogProps, DialogContent, DialogActions, IconButton } from '@indigo-ag/component-module';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';

import { getClient } from 'core/apollo/client';
import {
  createFinancingDatasetDocument,
  createFinancingKMLDocument,
  financingBrazilKMLPresignedUploadUrl,
  financingBrazilDatasetPresignedUploadUrl
} from '../../queries';
import { createStylesHook, EnhancedDialog, FinancingBrazilDataset, RemoteFinancingDocumentInfo } from 'shared';

interface GrowerReportUploadModalProps extends DialogProps {
  updateRemoteDataSetDocument: (newFile: FinancingBrazilDataset) => void;
  closeModal: () => void;
  remoteKmlDocument?: RemoteFinancingDocumentInfo;
}

interface FileData {
  file: any;
  name: string;
  type: string;
}

const useStyles = createStylesHook({
  checkbox: {
    margin: '0 5px 0 0'
  },
  checkboxContainer: {
    margin: '0 0 10px',
    padding: '0 10px 0 0',
    width: '50%'
  },
  formRow: {
    margin: '0 0 10px'
  },
  modalContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  modalContent: {
    maxWidth: '350px',
    padding: '20px',
    width: '100%'
  },
  permissionsContainer: {
    padding: '0 0 0 14px'
  },
  selectFilesDropbox: {
    border: `1px solid ${palettes.neutral[300]}`,
    color: palettes.legacy.blue.default,
    cursor: 'pointer',
    fontSize: '.7rem',
    fontWeight: 'bold',
    margin: '0 0 25px',
    padding: '2.5rem 0',
    textAlign: 'center',
    textTransform: 'uppercase'
  }
});

const GrowerReportUploadModal: React.FunctionComponent<GrowerReportUploadModalProps> = ({
  open,
  closeModal,
  updateRemoteDataSetDocument
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * State
   */
  const [fileData, setFileData] = useState<File>();
  const [fileName, setFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Mutations
   */
  const [createFinancingDatasetDocumentFn] = useMutation(createFinancingDatasetDocument);
  const [createFinancingKMLDocumentFn] = useMutation(createFinancingKMLDocument);

  const uploadFile = async ({ file, name, type }: FileData) => {
    const client = await getClient();
    if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setIsUploading(true);

      const options = {
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      };

      const getPresignedUrl = await client.query({
        fetchPolicy: 'no-cache',
        query: financingBrazilDatasetPresignedUploadUrl,
        variables: {
          data: {
            contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
      });

      await axios.put(getPresignedUrl.data.financingBrazilDatasetPresignedUploadUrl.presignedUrl, file, options);
      await createFinancingDatasetDocumentFn({
        variables: {
          data: {
            documentName: name,
            documentUrl: getPresignedUrl.data.financingBrazilDatasetPresignedUploadUrl.remoteFileName
          }
        }
      }).then(r => {
        updateRemoteDataSetDocument(r.data.createFinancingDatasetDocument);

        setFileData(undefined);
        setIsUploading(false);
        closeModal();
      });
    } else {
      setIsUploading(true);
      const options = {
        headers: {
          'Content-Type': 'text/xml'
        }
      };

      const getPresignedUrl = await client.query({
        fetchPolicy: 'no-cache',
        query: financingBrazilKMLPresignedUploadUrl,
        variables: {
          data: {
            contentType: 'text/xml'
          }
        }
      });

      await axios.put(getPresignedUrl.data.financingBrazilKMLPresignedUploadUrl.presignedUrl, file, options);
      await createFinancingKMLDocumentFn({
        variables: {
          data: {
            documentName: name,
            documentUrl: getPresignedUrl.data.financingBrazilKMLPresignedUploadUrl.remoteFileName
          }
        }
      }).then(() => {
        setFileData(undefined);
        setIsUploading(false);
        closeModal();
      });
    }
  };

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();

    uploadFile({
      file: fileData,
      name: fileName,
      type: fileData!.type
    });
  };

  const removeUploadedFile = () => {
    setFileData(undefined);
  };

  const onChooseFile = () => {
    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.click();
  };

  const onFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const file: File = event.dataTransfer.files[0];

    if (file) {
      setFileData(file);
    } else {
      alert(i18next.t('errorUploading'));
    }

    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.value = '';
  };

  const onDragOver = (e: any) => {
    const event = e as Event;
    event.stopPropagation();
    event.preventDefault();
  };

  const renderFileUpload = () => {
    if (fileData) {
      return (
        <div style={{ margin: '0 0 25px', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ textDecoration: 'underline' }}>{fileData.name}</span>
          <IconButton icon={faTrashAlt} size="regular" onClick={removeUploadedFile} />
        </div>
      );
    } else {
      return (
        <div onClick={onChooseFile} className={classes.selectFilesDropbox} onDrop={onFileDrop} onDragOver={onDragOver}>
          {i18next.t('selectDragFile')}
        </div>
      );
    }
  };

  const handleFileUpload = (e: any) => {
    const file: File = e.target.files[0];

    if (file) {
      setFileData(file);
      setFileName(file.name);
    } else {
      alert(i18next.t('errorUploading'));
    }

    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.value = '';
  };

  return (
    <EnhancedDialog title={i18next.t('uploadDocument')} open={open} onClose={closeModal}>
      <DialogContent>
        <input ref={fileInputRef} type="file" accept="*" onChange={handleFileUpload} style={{ display: 'none' }} />
        {renderFileUpload()}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} variant="secondary" size="small">
          {i18next.t('cancel')}
        </Button>
        <Button disabled={!fileData} onClick={handleUpload} busy={isUploading} size="small">
          {i18next.t('finish')}
        </Button>
      </DialogActions>
    </EnhancedDialog>
  );
};

export default GrowerReportUploadModal;
